import { useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';

import { ApiUrl } from '@common/enums';
import { SectionTypes } from '@common/enums/section-type-enum';
import { ApplianceModel, ApplianceSource } from '@common/models';
import { useFetchDependencies } from '@core/hooks/useFetchDependencies';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { skipToken } from '@reduxjs/toolkit/dist/query';

import {
    useFetchInStoreDishwashersQuery,
    useFetchProgramDishwashersQuery,
} from '../../../../../api/static-content-api/dishwasher-api/dishwasherService';
import { makeDishwashersSelector } from './dishwasher.utils';

/**
 * Defining empty array outside of selector as indicated in docs.
 * @see {@link https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#selecting-values-from-results}
 */
const emptyArray = [] satisfies ApplianceModel[];

export interface UseDishwasherReturn {
    browsedDishwashers: ApplianceModel[];
    isFetchingDishwashers: boolean;
    isErrorDishwashers: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const useDishwasher = (): UseDishwasherReturn => {
    const zipcode = useAppSelector(selectZipcode);
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();

    const inStoreDishwashersUrl = useRetailerSettings().getInStoreApplianceUrl(SectionTypes.dishwasher);

    const { isMaterialLoaded } = useMaterials();

    const { isFetchingDependencies } = useFetchDependencies();

    const selectInStoreDishwashersTransformed = useMemo(() => makeDishwashersSelector(), []);

    const {
        programDishwashers,
        isFetching: isFetchingprogramDishwashers,
        isError: isErrorprogramDishwashers,
    } = useFetchProgramDishwashersQuery(
        isFetchingDependencies ||
            !ApiUrl.DISHWASHERS ||
            !zipcode ||
            !selectedColor ||
            !selectedStore ||
            !isMaterialLoaded
            ? skipToken
            : {
                  params: {
                      zipCode: zipcode,
                      storeId: selectedStore?.companyId,
                      ...(selectedColor.qteGrpID ? { prodQteGrpID: selectedColor.qteGrpID } : {}),
                  },
              },
        {
            selectFromResult: ({ data, ...rest }) => ({
                programDishwashers:
                    data && !rest.isError
                        ? selectInStoreDishwashersTransformed(
                              data ?? emptyArray,
                              ApplianceSource.PROGRAM,
                              undefined,
                              true
                          )
                        : emptyArray,
                ...rest,
            }),
        }
    );

    const {
        inStoreDishwashers,
        isFetching: isFetchingInStoreDishwashers,
        isError: isErrorInStoreDishwashers,
    } = useFetchInStoreDishwashersQuery(
        isFetchingDependencies ||
            !inStoreDishwashersUrl ||
            !zipcode ||
            !selectedColor ||
            !selectedStore ||
            !isMaterialLoaded
            ? skipToken
            : { url: inStoreDishwashersUrl },
        {
            selectFromResult: ({ data, ...rest }) => ({
                inStoreDishwashers:
                    data && !rest.isError
                        ? selectInStoreDishwashersTransformed(
                              data ?? emptyArray,
                              ApplianceSource.IN_STORE,
                              undefined,
                              true
                          )
                        : emptyArray,
                ...rest,
            }),
        }
    );

    const browsedDishwashers = useMemo(() => {
        return [...programDishwashers, ...inStoreDishwashers];
    }, [inStoreDishwashers, programDishwashers]);

    return {
        browsedDishwashers,
        isFetchingDishwashers: isFetchingInStoreDishwashers || isFetchingprogramDishwashers,
        isErrorDishwashers: isErrorInStoreDishwashers || isErrorprogramDishwashers,
    };
};

export default useDishwasher;
